/**
 * src/modules/_datetime.ts — Sprint 25 (2026-05-05)
 *
 * Helpery formatowania ISO timestampów do polskiej strefy LOKALNEJ (Europe/Warsaw).
 *
 * Problem: backend zwraca timestampy w UTC (np. `2026-05-05T09:25:00.000Z`).
 * Wcześniejsze rozwiązania w SellBase robiły `iso.substring(11, 16)` co dawało
 * `09:25` — czyli czas UTC, a w Polsce CEST powinno być `11:25`.
 *
 * Wszystkie funkcje są bezpieczne na puste/zepsute wejście — zwracają pusty string
 * jeśli `Date(...)` da NaN, żeby nie wybuchł UI.
 *
 * Konwencje wyjścia (pl-PL, 24h, Europe/Warsaw):
 *   formatLocalTime("2026-05-05T09:25:00Z")     → "11:25"
 *   formatLocalDate("2026-05-05T22:30:00Z")     → "2026-05-06"  (po midnight UTC, +02:00)
 *   formatLocalDateTime("2026-05-05T09:25:00Z") → "2026-05-05 11:25"
 *   todayLocalISO()                             → "2026-05-05"  (dziś, lokalnie)
 */

const TZ = 'Europe/Warsaw';

// Pythonowe `str(datetime)` z backendu: „2026-10-07 09:15:23.123456+00:00” (spacja zamiast T,
// mikrosekundy, czasem strefa „+00” bez minut). Chrome to przełknie, Safari zwróci Invalid Date.
const _PY_DATETIME = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}(?::\d{2})?)(?:\.(\d+))?\s*(Z|[+-]\d{2}(?::?\d{2})?)?$/;

function _toDate(iso: any): Date | null {
  if (!iso) return null;
  if (iso instanceof Date) return isNaN(iso.getTime()) ? null : iso;
  let tekst = String(iso).trim();
  const m = _PY_DATETIME.exec(tekst);
  if (m) {
    const ms = m[3] ? '.' + (m[3] + '000').slice(0, 3) : '';
    let strefa = m[4] || '';
    if (/^[+-]\d{2}$/.test(strefa)) strefa += ':00';
    else if (/^[+-]\d{4}$/.test(strefa)) strefa = strefa.slice(0, 3) + ':' + strefa.slice(3);
    tekst = m[1] + 'T' + m[2] + ms + strefa;
  }
  const d = new Date(tekst);
  if (isNaN(d.getTime())) return null;
  return d;
}

/** "HH:MM" lokalnie. Pusty string przy złym wejściu. */
export function formatLocalTime(iso: any): string {
  const d = _toDate(iso);
  if (!d) return '';
  try {
    return d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: TZ });
  } catch (_) {
    return '';
  }
}

/** "YYYY-MM-DD" lokalnie. Pusty string przy złym wejściu. */
export function formatLocalDate(iso: any): string {
  const d = _toDate(iso);
  if (!d) return '';
  try {
    // pl-PL daje "DD.MM.YYYY", ale chcemy ISO-like dla porównań / sortowania → użyj sv-SE.
    return d.toLocaleDateString('sv-SE', { timeZone: TZ });
  } catch (_) {
    return '';
  }
}

/** "YYYY-MM-DD HH:MM" lokalnie. */
export function formatLocalDateTime(iso: any): string {
  const d = _toDate(iso);
  if (!d) return '';
  const date = formatLocalDate(d.toISOString());
  const time = formatLocalTime(d.toISOString());
  return date && time ? (date + ' ' + time) : (date || time);
}

/** Dzisiejsza data lokalna jako "YYYY-MM-DD" — alternatywa dla `new Date().toISOString().substring(0,10)`. */
export function todayLocalISO(): string {
  return formatLocalDate(new Date().toISOString());
}

/** Data sprzed N dni, lokalnie, jako "YYYY-MM-DD". */
export function localDateNDaysAgo(n: number): string {
  return formatLocalDate(new Date(Date.now() - n * 86400000).toISOString());
}

/** "DD.MM.YYYY" lokalnie — polski styl dla podpisów pod wiadomościami. */
export function formatLocalDateShortPL(iso: any): string {
  const d = _toDate(iso);
  if (!d) return '';
  try {
    return d.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: TZ });
  } catch (_) {
    return '';
  }
}

/**
 * "DD.MM.YYYY HH:MM" lokalnie (Europe/Warsaw) — data z godziną do tekstu dla człowieka.
 *
 * 07.10.2026 (audyt surowych dat): w UI lądowały timestampy wprost z API, np.
 * „Pomiar procesów od 2026-08-31T11:04:27.711342+00:00”. Ten helper jest dla nich
 * jedynym formatem „data + godzina” w zdaniach i komórkach. Złe/puste wejście → ''.
 */
export function formatLocalDateTimePL(iso: any): string {
  const d = _toDate(iso);
  if (!d) return '';
  const data = formatLocalDateShortPL(d.toISOString());
  const czas = formatLocalTime(d.toISOString());
  return data && czas ? (data + ' ' + czas) : (data || czas);
}

/** "DD.MM.YYYY HH:MM:SS" lokalnie — tam, gdzie liczą się sekundy (lista wiadomości, prośba z 04.05.2026). */
export function formatLocalDateTimeSecPL(iso: any): string {
  const d = _toDate(iso);
  if (!d) return '';
  try {
    const czas = d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: TZ });
    const data = formatLocalDateShortPL(d.toISOString());
    return data ? data + ' ' + czas : czas;
  } catch (_) {
    return '';
  }
}

// Czas ścienny bez strefy (np. „2026-10-07T13:04:00” policzony już w strefie reguł):
// tylko przestawienie pól, bez przeliczania — strefa przeglądarki nie może go przesunąć.
export function formatWallClockPL(wartosc: any): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(String(wartosc || ''));
  return m ? m[3] + '.' + m[2] + '.' + m[1] + ' ' + m[4] + ':' + m[5] : '';
}

// Globalne aliasy dla legacy app.js (inline onclick / bezpośredni window.X).
// `typeof window` — moduł importują też czyste buildery testowane w node (team-report.ts).
const _ns: any = typeof window !== 'undefined'
  ? ((window as any).SellBaseDT = (window as any).SellBaseDT || {})
  : {};
if (typeof _ns.formatLocalTime !== 'function') _ns.formatLocalTime = formatLocalTime;
if (typeof _ns.formatLocalDate !== 'function') _ns.formatLocalDate = formatLocalDate;
if (typeof _ns.formatLocalDateTime !== 'function') _ns.formatLocalDateTime = formatLocalDateTime;
if (typeof _ns.todayLocalISO !== 'function') _ns.todayLocalISO = todayLocalISO;
if (typeof _ns.formatLocalDateShortPL !== 'function') _ns.formatLocalDateShortPL = formatLocalDateShortPL;
if (typeof _ns.formatLocalDateTimePL !== 'function') _ns.formatLocalDateTimePL = formatLocalDateTimePL;
if (typeof _ns.formatLocalDateTimeSecPL !== 'function') _ns.formatLocalDateTimeSecPL = formatLocalDateTimeSecPL;
